import Answer from "../../entities/answer";

/**
 * Answer actions
 *
 * Call order:
 *
 * ANSWER_SAVE_QUEUE:	adds the item to the save queue
 * --> ANSWER_SAVE:	modifies the state
 * --> ANSWER_SAVE_SUCCESS: if all went well
 * --> ANSWER_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail ANSWER_SAVE_QUEUE is during the call to ANSWER_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at ANSWER_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: ANSWER_SAVE_SUCCESS, ANSWER_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or ANSWER_PENDING_CHECK (it left the queue).
 */

export const ANSWER_FETCH_ONE_QUEUE: any = 'ANSWER_FETCH_ONE_QUEUE';
export const ANSWER_FETCH_SUCCESS: any = 'ANSWER_FETCH_SUCCESS';
export const ANSWER_FETCH_FAILURE: any = 'ANSWER_FETCH_FAILURE';
export const ANSWER_FETCH_ONE_SUCCESS: any = 'ANSWER_FETCH_ONE_SUCCESS';
export const ANSWER_FETCH_ONE_FAILURE: any = 'ANSWER_FETCH_ONE_FAILURE';

export const ANSWER_SAVE: any = 'ANSWER_SAVE';
export const ANSWER_SAVE_QUEUE: any = 'ANSWER_SAVE_QUEUE';
export const ANSWER_SAVE_SUCCESS: any = 'ANSWER_SAVE_SUCCESS';
export const ANSWER_SAVE_FAILURE: any = 'ANSWER_SAVE_FAILURE';

export const ANSWER_SAVE_MANY: any = 'ANSWER_SAVE_MANY';
export const ANSWER_SAVE_MANY_QUEUE: any = 'ANSWER_SAVE_MANY_QUEUE';
export const ANSWER_SAVE_MANY_SUCCESS: any = 'ANSWER_SAVE_MANY_SUCCESS';
export const ANSWER_SAVE_MANY_FAILURE: any = 'ANSWER_SAVE_MANY_FAILURE';

export const ANSWER_REMOVE: any = 'ANSWER_REMOVE';
export const ANSWER_REMOVE_QUEUE: any = 'ANSWER_REMOVE_QUEUE';
export const ANSWER_REMOVE_SUCCESS: any = 'ANSWER_REMOVE_SUCCESS';
export const ANSWER_REMOVE_FAILURE: any = 'ANSWER_REMOVE_FAILURE';

export const ANSWER_RESTORE: any = 'ANSWER_RESTORE';
export const ANSWER_RESTORE_QUEUE: any = 'ANSWER_RESTORE_QUEUE';
export const ANSWER_RESTORE_SUCCESS: any = 'ANSWER_RESTORE_SUCCESS';
export const ANSWER_RESTORE_FAILURE: any = 'ANSWER_RESTORE_FAILURE';

export const ANSWER_RESTORE_MANY: any = 'ANSWER_RESTORE_MANY';
export const ANSWER_REMOVE_MANY: any = 'ANSWER_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const ANSWER_SAVE_REVERT: any = 'ANSWER_SAVE_REVERT';
export const ANSWER_PENDING_CHECK: any = 'ANSWER_PENDING_CHECK';

/* Fetch actions */
export const fetchAnswerSuccess = (answers: Answer[], partial: boolean, lastFetch: number) => ({type: ANSWER_FETCH_SUCCESS, answers, partial, lastFetch});
export const fetchAnswerFailure = (err: any) => {
		console.error('AnswerAction: fetchAnswerFailure', err);
		return {type: ANSWER_FETCH_FAILURE, err};
};
export const fetchOneAnswerQueue = (answerId: string, sentAddedOn?: number) => ({type: ANSWER_FETCH_ONE_QUEUE, answerId, sentAddedOn});
export const fetchOneAnswerSuccess = (answer: Answer | null, sentAddedOn?: number, answerId: string = answer?.id) => ({type: ANSWER_FETCH_ONE_SUCCESS, answerId, answer, sentAddedOn});
export const fetchOneAnswerFailure = (id: string, err: any) => {
		console.error('AnswerAction: fetchOneAnswerFailure', err);
		return {type: ANSWER_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveAnswer = (answer: Answer) => {
    // console.debug('⮂ ANSWER_SAVE action', answer?.id);
    return {type: ANSWER_SAVE, answer};
}
export const saveAnswerQueue = (answer: Answer, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ ANSWER_SAVE_QUEUE action', answer?.id);
    return {type: ANSWER_SAVE_QUEUE, answer, onQueued};
}
export const saveAnswerSuccess = (answer: Answer, sentAddedOn?: number) => {
    // console.debug('⮂ ANSWER_SAVE_SUCCESS action', answer?.id);
    return {type: ANSWER_SAVE_SUCCESS, answer, sentAddedOn};
}
export const saveAnswerFailure = (answer: Answer, status: number, message: string) => {
    console.warn(`⮂ ANSWER_SAVE_FAILURE error saving answer: HTTP ${status}: ${message}`, answer?.id);
    return {type: ANSWER_SAVE_FAILURE, answer, status, message};
};

/* SaveMany actions */
export const saveManyAnswers = (answers: Answer[]) => {
    // console.debug('⮂ ANSWER_SAVE_MANY action', answers?.map(answer => answer?.id).join(','));
    return {type: ANSWER_SAVE_MANY, answers};
}
export const saveManyAnswersQueue = (answers: Answer[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ ANSWER_SAVE_MANY_QUEUE action', answers?.map(answer => answer.id).join(','));
    return {type: ANSWER_SAVE_MANY_QUEUE, answers, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `answers` (OWA-1018) */
export const saveManyAnswersSuccess = (answers: Answer[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ ANSWER_SAVE_MANY_SUCCESS action', answers?.map(answer => answer.id).join(','));
    return {type: ANSWER_SAVE_MANY_SUCCESS, answers, sentAddedOns};
}
export const saveManyAnswersFailure = (answers: Answer[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_ANSWERS_FAILURE Error saving answers: HTTP ${status}: ${message}`, answers?.map(answer => answer.id).join(','));
		return {type: ANSWER_SAVE_MANY_FAILURE, answers, status, message};
};

/* Remove actions */
export const removeAnswer = (answer: Answer) => ({ type: ANSWER_REMOVE, answer });
export const removeAnswerQueue = (answer: Answer) => ({ type: ANSWER_REMOVE_QUEUE, answer });
export const removeAnswerSuccess = (answer: Answer) => ({ type: ANSWER_REMOVE_SUCCESS, answer });
export const removeAnswerFailure = (answer: Answer, status: number, message: string) => {
	console.error(`Redux: removeAnswerFailure Error removing answer: HTTP ${status}: ${message}`);
	return ({ type: ANSWER_REMOVE_FAILURE, answer, status, message });
};

/* Restore actions */
export const restoreAnswer = (answer: Answer) => ({ type: ANSWER_RESTORE, answer });
export const restoreAnswerQueue = (answer: Answer) => ({ type: ANSWER_RESTORE_QUEUE, answer });
export const restoreAnswerSuccess = (answer: Answer, sentAddedOn?: number) => ({ type: ANSWER_RESTORE_SUCCESS, answer, sentAddedOn });
export const restoreAnswerFailure = (answer: Answer, status: number, message: string) => {
	console.error(`Redux: restoreAnswerFailure Error restoring answer: HTTP ${status}: ${message}`);
	return ({ type: ANSWER_RESTORE_FAILURE, answer, status, message });
};

/* RestoreMany actions */
export const restoreManyAnswers = (answers: Answer[]) => ({ type: ANSWER_RESTORE_MANY, answers });

/* RemoveMany actions */
export const removeManyAnswers = (answers: Answer[]) => ({ type: ANSWER_REMOVE_MANY, answers });

/* Pending save actions (OWA-1018) */
/**
 * The save `answer` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertAnswerSave = (answer: Answer, options: {sentAddedOn?: number, current?: Answer | null, keep?: boolean} = {}) =>
    ({type: ANSWER_SAVE_REVERT, answer, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkAnswerPendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: ANSWER_PENDING_CHECK, live, refused, readAt});